import {
  reactive,
  UnwrapRef,
  Ref,
  ref,
  getCurrentInstance,
  ComponentInternalInstance,
  computed,
  ComputedRef,
} from '@vue/composition-api';
import dayjs from 'dayjs';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import user from '@/renderer/scripts/utils/user';
import { copyText2Clipboard } from '@/renderer/scripts/utils/tools/index';
import { getCustomDomainOrWebUrl } from '@/renderer/scripts/utils/CustomDomain/customDomain';
import { DomainItem } from '@/renderer/scripts/libs/smh-sdk-class/typings';

interface ColleborationUser {
  avatar: string;
  nickname: string;
  id: number;
  role: string;
  authRoleId?: number;
}
export enum GenerateCodeStatus {
  NOT_GENERATED = 'NOT_GENERATED',
  IS_STOP = 'IS_STOP',
  IS_START = 'IS_START',
}
type Handler = {
  loadingUrlInfo: Ref<boolean>;
  inviteExternalFrom: UnwrapRef<{
    authRoleId: number;
    allowExternalUser: boolean;
  }>;
  inviteInternalFrom: UnwrapRef<{
    users: ColleborationUser[];
  }>;
  inviteUrl: ComputedRef<string>;
  inviteUrlInfo: UnwrapRef<{
    groupName: string;
    codeStatus: GenerateCodeStatus;
    expireTime: string;
    expired: boolean;
    enabled: boolean;
    code: string;
    domains: DomainItem[];
    authRoleId: number;
    invitedCount: number;
    allowExternalUser: boolean;
    userLimitReached: boolean;
  }>;
  methods: {
    initData: (createImmediately?: boolean) => void;
    initInternalData: () => void;
    checkIfHasInviteUrl: () => Promise<void>;
    getInviteCodeInfo: (code: string) => Promise<void>;
    updateInviteCodeInfo: (type: 'stop' | 'recovery' | string | undefined, _params?: Object) => Promise<void>;
  };
};
export default function handler(group: Ref<SMH.ColleborationSpaceInfoData>): Handler {
  // 当前组件
  const currComponent = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;

  // const defaultRoleId = computed(() => vm.$store.getters.defaultRoleId);
  const editorRoleId = computed(() => vm.$store.getters.editorRole?.id);
  const inviteExternalFrom = reactive<Handler['inviteExternalFrom']>({
    authRoleId: editorRoleId.value,
    allowExternalUser: false,
  });
  const inviteUrl = computed(() => {
    const { code, domains } = inviteUrlInfo;
    const domain = getCustomDomainOrWebUrl(domains);
    const url = `${domain}/web/invitation/${encodeURIComponent(code as string)}`;
    return url;
  });
  const inviteInternalFrom = reactive<Handler['inviteInternalFrom']>({
    users: [],
  });
  const inviteUrlInfo = reactive({
    groupName: '',
    codeStatus: GenerateCodeStatus.NOT_GENERATED,
    expireTime: '',
    code: '',
    domains: [
      {
        domain: '',
        isCustom: false,
        status: 0,
      },
    ],
    enabled: true,
    expired: false,
    invitedCount: 0,
    allowExternalUser: false,
    userLimitReached: false,
    authRoleId: editorRoleId.value,
  });
  const loadingUrlInfo = ref(false);
  const methods = {
    async initData(createImmediately?: boolean) {
      loadingUrlInfo.value = true;
      await methods.checkIfHasInviteUrl();
      loadingUrlInfo.value = false;
      inviteUrlInfo.allowExternalUser = group.value.allowExternalUser;
      inviteUrlInfo.codeStatus === 'NOT_GENERATED' && createImmediately && (await methods.generateInviteUrl());
    },
    // 初始化当前已邀请的企业成员
    initInternalData() {
      const currUserInfo = vm.$store.getters.userBaseInfo;
      inviteInternalFrom.users = [
        {
          avatar: currUserInfo.avatar as string,
          nickname: currUserInfo.nickname as string,
          role: 'owner',
          id: currUserInfo.userId,
        },
      ];
      methods.checkIfHasInviteUrl();
    },
    async generateInviteUrl() {
      reporter.report('group', {
        group_invite: 'outer',
        group_name: group.value.name,
      });
      try {
        const params = {
          groupId: group.value.id,
          authRoleId: editorRoleId.value,
        };
        const {
          data: { code },
        } = await smh.userAPI.generateGroupInviteCode(params);
        await methods.getInviteCodeInfo(code);
        const copyContent = i18n_t('点击链接加入我的“{slot1}”协作群组，一起开启文件协作管理新体验吧！链接：{slot2}', {
          slot1: inviteUrlInfo.groupName,
          slot2: inviteUrl.value,
        });
        copyText2Clipboard(copyContent)
          .then(() => {
            tips.success(i18n_t('邀请链接已复制到剪切板'));
          })
          .catch(() => {
            tips.error(i18n_t('邀请链接复制失败'));
          });
      } catch (error) {
        tips.error(i18n_t('生成邀请链接失败'));
      }
    },
    async checkIfHasInviteUrl() {
      try {
        // 1. 查询邀请码
        const {
          data: { code },
        } = await smh.userAPI.getGroupInviteCode({
          groupId: group.value.id,
        });
        // 2. 查询邀请码是否已停止
        methods.getInviteCodeInfo(code);
      } catch (error) {
        inviteUrlInfo.codeStatus = GenerateCodeStatus.NOT_GENERATED;
      }
    },
    async getInviteCodeInfo(code: string) {
      try {
        const { data: codeInfo } = await smh.userAPI.getGroupInviteInfo({
          code,
        });
        // 拿到当前企业的配置信息，这个是可加入的用户数量
        const { shareGroupUserCountLimit } = user.getUserInfo().editionConfig;
        if (codeInfo.enabled && !codeInfo.expired) {
          inviteUrlInfo.codeStatus = GenerateCodeStatus.IS_START;
        } else {
          inviteUrlInfo.codeStatus = GenerateCodeStatus.IS_STOP;
        }
        inviteUrlInfo.enabled = codeInfo.enabled;
        inviteUrlInfo.expired = codeInfo.expired;
        inviteUrlInfo.code = codeInfo.code;
        inviteUrlInfo.domains = codeInfo.domains;
        inviteUrlInfo.expireTime = codeInfo.expireTime;
        inviteUrlInfo.invitedCount = codeInfo.invitedCount;
        inviteUrlInfo.allowExternalUser = codeInfo.allowExternalUser;
        inviteUrlInfo.userLimitReached = codeInfo.currentGroupUserCount >= shareGroupUserCountLimit;
        inviteUrlInfo.groupName = codeInfo.group.name;
        inviteUrlInfo.authRoleId = codeInfo.authRoleId;
      } catch (error) {
        inviteUrlInfo.codeStatus = GenerateCodeStatus.NOT_GENERATED;
      }
    },
    async updateInviteCodeInfo(type: 'stop' | 'recovery' | string | undefined, _params?: Object) {
      const params = {
        code: inviteUrlInfo.code,
      };
      if (type === 'stop') {
        Object.assign(params, {
          expireTime: dayjs(Date.now()).format('YYYY-MM-DD HH:mm'),
          enabled: false,
        });
      } else if (type === 'recovery') {
        Object.assign(params, {
          expireTime: dayjs(Date.now()).add(1, 'days').format('YYYY-MM-DD HH:mm'),
          enabled: true,
        });
      } else {
        Object.assign(params, _params);
      }
      try {
        await smh.userAPI.updateInviteCode(params);
        methods.getInviteCodeInfo(inviteUrlInfo.code);
      } catch (error) {
        tips.error(error as MsgError);
      }
    },
  };
  return {
    loadingUrlInfo,
    inviteUrl,
    inviteUrlInfo,
    inviteExternalFrom,
    inviteInternalFrom,
    methods,
  };
}
